import { useEffect, useState } from 'react'
import { api } from '../api'
import { theme } from '../theme'
import { Button, Card, Heading, Page } from '../components/ui'
import { Loading } from '../components/States'
import { fromServer, t } from '../lib/i18n'

/**
 * The page a consent email opens (email plus, services/consent.py, 29 Oct 2026).
 *
 * `?consent=TOKEN` from the first email: "Yes, I agree". `?withdraw=TOKEN`
 * from the second: "Withdraw and delete". Nothing happens on opening the
 * page, only on the press: mail scanners open links by themselves, and an
 * opened link must never count as consent, or delete anything.
 *
 * It works signed out, on any device: the token is the proof.
 */
export function ConsentLink({
  kind, token, onDone,
}: { kind: 'consent' | 'withdraw'; token: string; onDone: () => void }) {
  const [info, setInfo] = useState<{ valid: boolean; email?: string; children?: number } | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.consentLink(token, kind).then(setInfo).catch(() => setInfo({ valid: false }))
  }, [token, kind])

  if (!info) return <Loading grownUp />

  const press = async () => {
    setBusy(true)
    setProblem(null)
    try {
      if (kind === 'consent') {
        await api.confirmConsent(token)
        setDone(t('Thank you. Your child can start now. Go back to Word Sprout on the device where you signed in, or open it here.'))
      } else {
        await api.withdrawConsent(token)
        setDone(t("Done. Everything about your children has been deleted. We have sent you an email to say so."))
      }
    } catch (err) {
      setProblem(fromServer(err, 'That did not work. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page width={560}>
      <Heading>
        {kind === 'consent' ? t('Your consent for Word Sprout') : t('Withdraw your consent')}
      </Heading>
      <Card>
        {!info.valid && !done ? (
          <p style={{ margin: 0, fontSize: 16, color: theme.text, lineHeight: 1.55 }}>
            {t('This link has expired or was already used. Open Word Sprout to get a new one.')}
          </p>
        ) : done ? (
          <p style={{ margin: 0, fontSize: 16, color: theme.text, lineHeight: 1.55 }}>{done}</p>
        ) : kind === 'consent' ? (
          <>
            <p style={{ margin: '0 0 14px', fontSize: 16, color: theme.text, lineHeight: 1.55 }}>
              {t('Do you agree that Word Sprout may collect what the email described from your child (first name, school year, pictures, PIN and answers) to choose their practice and show you their progress?')}
            </p>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: theme.muted, lineHeight: 1.5 }}>
              {t('For {email}. You can withdraw at any time; a second email in a day or so says how.', { email: info.email ?? '' })}
            </p>
            <Button full onClick={() => void press()} disabled={busy}>{t('Yes, I agree')}</Button>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 18px', fontSize: 16, color: theme.text, lineHeight: 1.55 }}>
              {t('This deletes everything about your {n} children at once: names, pictures, answers and progress. It cannot be undone. Your own sign-in stays.', { n: info.children ?? 0 })}
            </p>
            <Button full tone="ghost" onClick={() => void press()} disabled={busy}
              style={{ color: theme.red, borderColor: theme.red }}>
              {t('Withdraw and delete')}
            </Button>
          </>
        )}
        {problem ? <p style={{ margin: '12px 0 0', color: theme.red, fontSize: 14 }}>{problem}</p> : null}
      </Card>
      <div style={{ marginTop: 14 }}>
        <Button tone="quiet" onClick={onDone}>{t('Open Word Sprout')}</Button>
      </div>
    </Page>
  )
}
